import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import emailValidator from 'email-validator';
import { TextField } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Footer, FormTitle, FooterText, FormFields, FooterLink, ErrorMessage, FormContainer, FormDescription, SubmitButtonField, VerticalFormFields } from '../components/FormComponents';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
export const GetResetPasswordScreenTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ResetPasswordScreenTID: {
                SentMessage: 'reset-email-sent-msg',
                SentDescription: 'reset-password-sent-description',
                Form: 'reset-password-form',
                EmailInput: 'email-input',
                SubmitButton: 'reset-password-button',
                Error: 'error-msg',
                BackToLogin: 'back-to-login-button'
            }
        }
    });
const SentScreen = (param)=>{
    let { email, tryAgain } = param;
    const { t: resetPasswordTexts } = useLocaleKeys('pages.auth.resetPassword');
    const { ResetPasswordScreenTID } = useMemo(()=>GetResetPasswordScreenTID(), []);
    return /*#__PURE__*/ _jsx(UnauthorizedPageContainer, {
        testId: ResetPasswordScreenTID.SentMessage(),
        children: /*#__PURE__*/ _jsxs(FormContainer, {
            children: [
                /*#__PURE__*/ _jsx(FormTitle, {
                    children: resetPasswordTexts.checkYourEmail()
                }),
                /*#__PURE__*/ _jsx(FormDescription, {
                    "data-testid": ResetPasswordScreenTID.SentDescription(),
                    children: resetPasswordTexts.sentDescription({
                        email
                    })
                }),
                /*#__PURE__*/ _jsxs(Footer, {
                    children: [
                        /*#__PURE__*/ _jsx(FooterText, {
                            children: resetPasswordTexts.didntReceive()
                        }),
                        /*#__PURE__*/ _jsx(FooterLink, {
                            onClick: tryAgain,
                            children: resetPasswordTexts.wrongEmail()
                        })
                    ]
                })
            ]
        })
    });
};
const FormScreen = (param)=>{
    let { onSuccess } = param;
    const { t: resetPasswordTexts } = useLocaleKeys('pages.auth.resetPassword');
    const { api, logBI } = useStoreActions();
    const { ResetPasswordScreenTID } = useMemo(()=>GetResetPasswordScreenTID(), []);
    const [email, setEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [emailWasBlurred, setEmailWasBlurred] = useState(false);
    const [resetInProgress, setResetInProgress] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState(null);
    const showEmailFieldError = email !== '' && !isEmailValid && emailWasBlurred;
    return /*#__PURE__*/ _jsx(UnauthorizedPageContainer, {
        testId: ResetPasswordScreenTID.Form(),
        children: /*#__PURE__*/ _jsxs(FormContainer, {
            onChange: ()=>setResetPasswordError(null),
            onSubmit: async (event)=>{
                logBI(10600, {});
                event.preventDefault();
                setResetInProgress(true);
                try {
                    await api.auth.resetPassword(email);
                    setResetInProgress(false);
                    onSuccess(email);
                } catch (e) {
                    setResetInProgress(false);
                    setResetPasswordError(resetPasswordTexts.failedToReset());
                }
            },
            children: [
                /*#__PURE__*/ _jsx(FormTitle, {
                    children: resetPasswordTexts.title()
                }),
                /*#__PURE__*/ _jsx(FormDescription, {
                    children: resetPasswordTexts.formDescription()
                }),
                /*#__PURE__*/ _jsx(FormFields, {
                    children: /*#__PURE__*/ _jsx(VerticalFormFields, {
                        children: /*#__PURE__*/ _jsx(TextField, {
                            bi: {
                                name: 'reset_password_email'
                            },
                            collapseHelperText: false,
                            autoFocus: true,
                            label: resetPasswordTexts.emailLabel(),
                            type: "email",
                            value: email,
                            onChange: (event)=>{
                                const value = event.target.value;
                                const isValid = emailValidator.validate(value);
                                setEmail(value);
                                setIsEmailValid(isValid);
                            },
                            error: showEmailFieldError,
                            onBlur: ()=>setEmailWasBlurred(true),
                            testId: ResetPasswordScreenTID.EmailInput()
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(SubmitButtonField, {
                    disabled: !isEmailValid,
                    isLoading: resetInProgress,
                    testId: ResetPasswordScreenTID.SubmitButton(),
                    children: resetPasswordTexts.sendResetLink()
                }),
                resetPasswordError && /*#__PURE__*/ _jsx(ErrorMessage, {
                    testId: ResetPasswordScreenTID.Error(),
                    children: resetPasswordError
                }),
                /*#__PURE__*/ _jsx(Footer, {
                    children: /*#__PURE__*/ _jsx(FooterLink, {
                        toInternalLink: routes.login,
                        testId: ResetPasswordScreenTID.BackToLogin(),
                        children: resetPasswordTexts.backToSignIn()
                    })
                })
            ]
        })
    });
};
export function ResetPasswordScreen() {
    const [email, setEmail] = useState(null);
    useSetDocumentTitle('Anchor Reset Password');
    return email ? /*#__PURE__*/ _jsx(SentScreen, {
        email: email,
        tryAgain: ()=>setEmail(null)
    }) : /*#__PURE__*/ _jsx(FormScreen, {
        onSuccess: setEmail
    });
}
